import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import {
  BsFacebook,
  BsInstagram,
  BsLinkedin,
  BsTiktok,
  BsTwitter,
  BsPatchCheckFill,
  BsArrowRight,
  BsPersonX,
  BsEnvelope,
  BsTelephone,
} from 'react-icons/bs'
import Header from '../../layouts/Header'
import { API_URL } from '../../config'
import '../../App.css'
import '../../Theme.css'
import '../../FeesPage.css'
import './AffiliatePublicProfile.css'

const WS = `${API_URL}/apps/kscanner/webservices.php`

// Folder that profile photos are served from, used when the DB value is only a filename.
// Change this to match where your uploads live (must end with "/").
const PHOTO_BASE = `${API_URL}/`

const SOCIALS = [
  ['instagram', 'Instagram', <BsInstagram key="i" />],
  ['tiktok', 'TikTok', <BsTiktok key="t" />],
  ['x', 'X', <BsTwitter key="x" />],
  ['facebook', 'Facebook', <BsFacebook key="f" />],
  ['linkedin', 'LinkedIn', <BsLinkedin key="l" />],
]

const isHttps = (u) => typeof u === 'string' && /^https:\/\//i.test(u)

const photoSrc = (p) => {
  if (!p) return ''
  const v = String(p).trim()
  if (/^https?:\/\//i.test(v)) return v.replace(/^http:/i, 'https:')
  return `${PHOTO_BASE}${v.replace(/^\/+/, '')}`
}

const Avatar = ({ src, name }) => {
  const [failed, setFailed] = useState(false)
  if (src && !failed) {
    return <img className="ap-avatar" src={src} alt="" onError={() => setFailed(true)} />
  }
  return (
    <span className="ap-avatar ap-avatar--i" aria-hidden="true">
      {name.charAt(0)}
    </span>
  )
}

const AffiliatePublicProfile = ({ match, history }) => {
  const { affiliateid } = match.params
  const [data, setData] = useState(null)
  const [state, setState] = useState('loading') // loading | ok | missing

  useEffect(() => {
    document.title = 'Karamale Partner'
    // keep attribution for the signup flow
    const stored = localStorage.getItem('affiliateid')
    if (!stored || stored === 'null') localStorage.setItem('affiliateid', affiliateid)

    let alive = true
    axios
      .get(WS, { params: { query: 'AFFILIATEPROFILE', affiliateid } })
      .then((res) => {
        if (!alive) return
        if (res.data && res.data.name) {
          setData(res.data)
          setState('ok')
        } else setState('missing')
      })
      .catch(() => alive && setState('missing'))
    return () => {
      alive = false
    }
  }, [affiliateid])

  const signUp = () => history.push({ pathname: '/organiser-registration' })

  if (state === 'loading') {
    return (
      <div className="fp-page ap-page">
        <Header />
        <main className="fp-wrap ap-wrap">
          <div className="ap-skel" aria-busy="true" aria-label="Loading profile" />
        </main>
      </div>
    )
  }

  if (state === 'missing') {
    return (
      <div className="fp-page ap-page">
        <Header />
        <main className="fp-wrap ap-wrap">
          <div className="ap-empty">
            <span className="ap-empty__icon">
              <BsPersonX aria-hidden="true" />
            </span>
            <h1 className="ap-empty__title">This partner profile isn't available</h1>
            <p className="ap-empty__text">
              It may be private, or the link may be incorrect. You can still explore Karamale or
              start selling tickets.
            </p>
            <div className="ap-empty__actions">
              <Link to="/events" className="fp-btn fp-btn--brand">
                Explore events
              </Link>
              <Link to="/organiser-registration" className="fp-btn fp-btn--ghost">
                Sell tickets
              </Link>
            </div>
          </div>
        </main>
      </div>
    )
  }

  const first = data.name.split(' ')[0]
  const socials = SOCIALS.filter(([k]) => isHttps(data.socials && data.socials[k]))
  const figures = []
  if (data.followers) {
    figures.push([String(data.followers).replace(/\s*-\s*/, '–'), 'followers, self-reported'])
  }
  if (data.stats) {
    if (data.stats.organisers) figures.push([data.stats.organisers, 'organisers referred'])
    if (data.stats.events) figures.push([data.stats.events, 'events from referrals'])
  }
  const hasStub = figures.length > 0
  const hasContact = data.contact && (data.contact.email || data.contact.phone)
  const role = `${data.type ? `${data.type} partner` : 'Partner'}${
    data.since ? ` since ${data.since}` : ''
  }`

  return (
    <div className="fp-page ap-page">
      <Header />
      <main className="fp-wrap ap-wrap">
        <div className="ap-passwrap">
          <section className={`ap-pass ${hasStub ? 'has-stub' : ''}`}>
            <div className="ap-pass__main">
              <Avatar src={photoSrc(data.photo)} name={data.name} />
              <div>
                <h1 className="ap-name">{data.name}</h1>
                {data.verified && (
                  <span className="ap-badge">
                    <BsPatchCheckFill aria-hidden="true" /> Verified partner
                  </span>
                )}
                <p className="ap-meta">{role}</p>
              </div>
            </div>

            {hasStub && (
              <div className="ap-pass__stub">
                {figures.map(([n, label]) => (
                  <div key={label} className="ap-fig">
                    <strong>{n}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {(data.bio || data.pitch) && (
          <section className="ap-about">
            {data.bio && (
              <>
                <h2>About {first}</h2>
                <p>{data.bio}</p>
              </>
            )}
            {data.pitch && (
              <>
                <h2>Why work with {first}</h2>
                <p>{data.pitch}</p>
              </>
            )}
          </section>
        )}

        {socials.length > 0 && (
          <section className="ap-links">
            <h2>Find {first} online</h2>
            <div className="ap-linkrow">
              {socials.map(([k, label, icon]) => (
                <a
                  key={k}
                  href={data.socials[k]}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  {icon} {label}
                </a>
              ))}
            </div>
          </section>
        )}

        {hasContact && (
          <section className="ap-links">
            <h2>Contact {first}</h2>
            <div className="ap-linkrow">
              {data.contact.email && (
                <a href={`mailto:${data.contact.email}`}>
                  <BsEnvelope aria-hidden="true" /> {data.contact.email}
                </a>
              )}
              {data.contact.phone && (
                <a href={`tel:${data.contact.phone}`}>
                  <BsTelephone aria-hidden="true" /> {data.contact.phone}
                </a>
              )}
            </div>
          </section>
        )}

        <section className="ap-join">
          <div>
            <h2>Sell your tickets on Karamale</h2>
            <p>
              {first} invited you. Create an organiser account, publish your event and get paid with
              transparent payouts.
            </p>
          </div>
          <button type="button" className="fp-btn fp-btn--light" onClick={signUp}>
            Sign up as an organiser <BsArrowRight aria-hidden="true" />
          </button>
        </section>
      </main>
    </div>
  )
}

export default AffiliatePublicProfile
